/**
 * ASP (Automated System Processes) Tab
 *
 * First section: NK-CAT Practitioner Library curation dashboard.
 * Shows cycle history, daily pulse chart, error gauge, and summary stats.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { storage } from '../../services/storage';
import { API_BASE_URL } from '../../utils/apiConfig';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  RefreshCw,
  Database,
  DollarSign,
  AlertTriangle,
  Activity,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// ── API helper ──────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string): Promise<T> {
  const token = await storage.getItem('admin_token');
  if (!token) throw new Error('Admin token not found');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

// ── Types ────────────────────────────────────────────────────────────────────

interface Cycle {
  id: string;
  agent_type: string;
  trigger_type: string;
  category: string | null;
  entries_created: number;
  entries_updated: number;
  entries_skipped: number;
  perplexity_calls: number;
  tavily_calls: number;
  nano_calls: number;
  cost: number;
  duration: number;
  status: string;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
}

interface CyclesResponse {
  success: boolean;
  data: {
    cycles: Cycle[];
    total_count: number;
    page: number;
    per_page: number;
  };
  stats: {
    cycles_7d: number;
    entries_created_7d: number;
    entries_skipped_7d: number;
    entries_updated_7d: number;
    cost_7d: number;
    duration_7d: number;
    failed_7d: number;
    perplexity_calls_7d: number;
    tavily_calls_7d: number;
    nano_calls_7d: number;
    error_rate_24h: number;
    total_24h: number;
    failed_24h: number;
  };
}

interface DailyPoint {
  date: string;
  total_cycles: number;
  entries_created: number;
  entries_skipped: number;
  entries_updated: number;
  failed_runs: number;
  total_cost: number;
  total_duration: number;
  perplexity_calls: number;
  tavily_calls: number;
  nano_calls: number;
}

interface DailyStatsResponse {
  success: boolean;
  data: DailyPoint[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function getErrorGauge(rate: number): { color: string; bg: string; label: string } {
  if (rate <= 5) return { color: 'var(--status-success)', bg: 'var(--status-success-bg)', label: 'Healthy' };
  if (rate <= 15) return { color: 'var(--status-warning)', bg: 'var(--status-warning-bg)', label: 'Elevated' };
  return { color: 'var(--status-error)', bg: 'var(--status-error-bg)', label: 'Critical' };
}

const STATUS_COLORS: Record<string, string> = {
  completed: 'var(--status-success)',
  running: 'var(--status-info)',
  failed: 'var(--status-error)',
  partial: 'var(--status-warning)',
};

const AGENT_LABELS: Record<string, string> = {
  scout: 'Scout',
  researcher: 'Researcher',
  editor: 'Editor',
};

const TRIGGER_LABELS: Record<string, string> = {
  cron: 'Cron',
  scout_event: 'Scout Event',
  admin: 'Manual',
  gap_detection: 'Gap Detect',
};

// ── Component ────────────────────────────────────────────────────────────────

export const ASPTab: React.FC = () => {
  const [cyclesData, setCyclesData] = useState<CyclesResponse | null>(null);
  const [dailyData, setDailyData] = useState<DailyPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cycles, daily] = await Promise.all([
        apiFetch<CyclesResponse>(`/api/admin/asp/nkcat/cycles?page=${page}&per_page=25`),
        apiFetch<DailyStatsResponse>('/api/admin/asp/nkcat/daily-stats?days=30'),
      ]);
      setCyclesData(cycles);
      setDailyData(daily.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load ASP data');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading && !cyclesData) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        Loading system processes...
      </div>
    );
  }

  if (error && !cyclesData) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--status-error)' }}>
        <AlertTriangle size={24} style={{ margin: '0 auto 12px' }} />
        {error}
        <button onClick={loadData} style={{ display: 'block', margin: '12px auto', color: 'var(--status-info)', cursor: 'pointer', background: 'none', border: 'none' }}>
          Retry
        </button>
      </div>
    );
  }

  const stats = cyclesData?.stats;
  const cycles = cyclesData?.data.cycles || [];
  const totalCount = cyclesData?.data.total_count || 0;
  const totalPages = Math.ceil(totalCount / 25);
  const gauge = getErrorGauge(stats?.error_rate_24h || 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Section Header */}
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--surface-subtle)', margin: 0 }}>
          NK-CAT Practitioner Library
        </h2>
        <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 4 }}>
          Automated curation: scout scans every 4h, daily refresh at 3 AM UTC. Finds new sources, validates links, removes outdated entries.
        </p>
      </div>

      {/* Summary Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <Card>
            <CardContent style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Activity size={16} color="var(--status-info)" />
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cycles (7d)</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--surface-subtle)' }}>{stats.cycles_7d}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                {stats.failed_7d} failed
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Database size={16} color="var(--status-success)" />
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Entries Created (7d)</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--surface-subtle)' }}>{stats.entries_created_7d}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                {stats.entries_skipped_7d} skipped &middot; {stats.entries_updated_7d} updated
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <DollarSign size={16} color="var(--status-warning)" />
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cost (7d)</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--surface-subtle)' }}>${stats.cost_7d.toFixed(2)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                {stats.perplexity_calls_7d} Perplexity &middot; {stats.tavily_calls_7d} Tavily &middot; {stats.nano_calls_7d} Nano
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <AlertTriangle size={16} color={gauge.color} />
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Error Rate (24h)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--surface-subtle)' }}>{stats.error_rate_24h}%</div>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 20,
                  backgroundColor: gauge.bg, color: gauge.color,
                  fontSize: 12, fontWeight: 600,
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: gauge.color }} />
                  {gauge.label}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                {stats.failed_24h} of {stats.total_24h} runs
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Daily Pulse Chart */}
      {dailyData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: 16 }}>Daily Pulse — Last 30 Days</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--text-strong)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }}
                  tickFormatter={(v) => {
                    const d = new Date(v);
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'var(--status-error)' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--text-primary)', border: '1px solid var(--text-strong)', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: 'var(--surface-subtle)' }}
                  labelFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="entries_created"
                  name="Created"
                  stroke="var(--status-success)"
                  fill="var(--status-success)"
                  fillOpacity={0.19}
                  strokeWidth={2}
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="entries_skipped"
                  name="Skipped"
                  stroke="var(--status-warning)"
                  fill="var(--status-warning)"
                  fillOpacity={0.125}
                  strokeWidth={2}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="failed_runs"
                  name="Errors"
                  stroke="var(--status-error)"
                  strokeWidth={2}
                  dot={{ r: 3, fill: 'var(--status-error)' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Cycle History Table */}
      <Card>
        <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <CardTitle style={{ fontSize: 16 }}>Cycle History</CardTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-tertiary)' }}>
            <span>{totalCount} total runs</span>
            {totalPages > 1 && (
              <>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{ background: 'none', border: 'none', cursor: page === 1 ? 'default' : 'pointer', color: page === 1 ? 'var(--text-strong)' : 'var(--text-tertiary)', padding: 4 }}
                >
                  <ChevronLeft size={16} />
                </button>
                <span>{page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{ background: 'none', border: 'none', cursor: page === totalPages ? 'default' : 'pointer', color: page === totalPages ? 'var(--text-strong)' : 'var(--text-tertiary)', padding: 4 }}
                >
                  <ChevronRight size={16} />
                </button>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--text-strong)' }}>
                  {['Time', 'Agent', 'Trigger', 'Category', 'Created', 'Skipped', 'Cost', 'Duration', 'Status', 'Error'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cycles.map((c) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--text-primary)' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--border-strong)', whiteSpace: 'nowrap' }}>{formatTime(c.started_at)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, backgroundColor: 'var(--text-strong)', color: 'var(--border-strong)' }}>
                        {AGENT_LABELS[c.agent_type] || c.agent_type}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, backgroundColor: 'var(--text-primary)', color: 'var(--text-tertiary)' }}>
                        {TRIGGER_LABELS[c.trigger_type] || c.trigger_type}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--text-tertiary)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.category || '—'}
                    </td>
                    <td style={{ padding: '8px 12px', color: c.entries_created > 0 ? 'var(--status-success)' : 'var(--text-secondary)', fontWeight: c.entries_created > 0 ? 600 : 400 }}>
                      {c.entries_created}
                    </td>
                    <td style={{ padding: '8px 12px', color: c.entries_skipped > 0 ? 'var(--status-warning)' : 'var(--text-secondary)' }}>
                      {c.entries_skipped}
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--border-strong)' }}>${c.cost.toFixed(3)}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--text-tertiary)' }}>{formatDuration(c.duration)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        color: STATUS_COLORS[c.status] || 'var(--text-tertiary)',
                        backgroundColor: (STATUS_COLORS[c.status] || 'var(--text-tertiary)') + '20',
                      }}>
                        {c.status}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--status-error)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.error || ''}>
                      {c.error || '—'}
                    </td>
                  </tr>
                ))}
                {cycles.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
                      No cycle runs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ASPTab;
